import { useEffect, useState } from "react";

// Appearance settings: mode (light|dark|system|schedule), schedule window, density (comfortable|compact), fontScale (standard|large), contrast (standard|high)
const K_MODE = "ali_theme_mode", K_SCHED = "ali_theme_sched", K_DENSITY = "ali_density", K_LEGACY = "ali_theme", K_FONT = "ali_font_scale", K_CONTRAST = "ali_contrast";
export const THEME_MODES = ["light", "dark", "system", "schedule"];
const DEFAULT_SCHED = { from: "19:00", to: "07:00" };

const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch {} };

export function getSettings() {
  let mode = safeGet(K_MODE);
  if (!THEME_MODES.includes(mode)) mode = safeGet(K_LEGACY) === "dark" ? "dark" : "light";
  let schedule = DEFAULT_SCHED;
  try { const s = JSON.parse(safeGet(K_SCHED)); if (s && s.from && s.to) schedule = s; } catch {}
  const density = safeGet(K_DENSITY) === "compact" ? "compact" : "comfortable";
  const fontScale = safeGet(K_FONT) === "large" ? "large" : "standard";
  const contrast = safeGet(K_CONTRAST) === "high" ? "high" : "standard";
  return { mode, schedule, density, fontScale, contrast };
}

const toMin = (hhmm) => { const [h, m] = String(hhmm || "0:0").split(":").map((x) => parseInt(x, 10) || 0); return h * 60 + m; };
export function inDarkWindow(schedule, now = new Date()) {
  const cur = now.getHours() * 60 + now.getMinutes(), a = toMin(schedule.from), b = toMin(schedule.to);
  return a <= b ? cur >= a && cur < b : cur >= a || cur < b; // overnight window when from > to
}

export function resolveTheme({ mode, schedule }) {
  if (mode === "dark") return "dark";
  if (mode === "system") return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  if (mode === "schedule") return inDarkWindow(schedule) ? "dark" : "light";
  return "light";
}

function paint(theme) {
  const dark = theme === "dark";
  document.documentElement.classList.toggle("dark", dark);
  document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
  safeSet(K_LEGACY, dark ? "dark" : "light");
  window.dispatchEvent(new CustomEvent("ali-theme", { detail: dark ? "dark" : "light" }));
}

// 自动切换提示：定时 / 跟随系统 导致主题变化时记录一次（手动选 浅色 / 深色 不提示）
let autoHint = null;
const currentTheme = () => document.documentElement.getAttribute("data-theme") || safeGet(K_LEGACY);
function paintAuto(s) {
  const prev = currentTheme(), next = resolveTheme(s);
  paint(next);
  if ((s.mode === "schedule" || s.mode === "system") && prev && prev !== next) {
    autoHint = { theme: next, mode: s.mode, schedule: s.schedule, at: Date.now() };
    window.dispatchEvent(new CustomEvent("ali-theme-auto", { detail: autoHint }));
  }
}
export function takeAutoThemeHint() { const h = autoHint; autoHint = null; return h; }

let timer = null, mq = null;
function rearm() {
  if (timer) { clearInterval(timer); timer = null; }
  if (mq) { mq.onchange = null; mq = null; }
  const s = getSettings();
  if (s.mode === "schedule") timer = setInterval(() => paintAuto(getSettings()), 30_000);
  if (s.mode === "system" && window.matchMedia) { mq = window.matchMedia("(prefers-color-scheme: dark)"); mq.onchange = () => paintAuto(getSettings()); }
}

export function applySettings({ mode, schedule, density, fontScale, contrast } = {}) {
  if (mode && THEME_MODES.includes(mode)) safeSet(K_MODE, mode);
  if (schedule && schedule.from && schedule.to) safeSet(K_SCHED, JSON.stringify(schedule));
  if (density) safeSet(K_DENSITY, density === "compact" ? "compact" : "comfortable");
  if (fontScale) safeSet(K_FONT, fontScale === "large" ? "large" : "standard");
  if (contrast) safeSet(K_CONTRAST, contrast === "high" ? "high" : "standard");
  const s = getSettings();
  document.documentElement.setAttribute("data-density", s.density);
  document.documentElement.setAttribute("data-font", s.fontScale);
  document.documentElement.setAttribute("data-contrast", s.contrast);
  paintAuto(s);
  rearm();
  window.dispatchEvent(new CustomEvent("ali-appearance", { detail: s }));
}

// legacy helper (explicit light/dark)
export function applyTheme(t) { applySettings({ mode: t === "dark" ? "dark" : "light" }); }

export function useTheme() {
  const [theme, setTheme] = useState(() => document.documentElement.classList.contains("dark") ? "dark" : "light");
  useEffect(() => {
    const h = (e) => setTheme(e.detail);
    window.addEventListener("ali-theme", h);
    return () => window.removeEventListener("ali-theme", h);
  }, []);
  return [theme, (t) => applyTheme(typeof t === "function" ? t(theme) : t)];
}

export function useAppearance() {
  const [settings, set] = useState(getSettings);
  useEffect(() => {
    const h = (e) => set(e.detail);
    window.addEventListener("ali-appearance", h);
    return () => window.removeEventListener("ali-appearance", h);
  }, []);
  return [settings, applySettings];
}

applySettings();
